import { BaseSyntheticEvent, useEffect, useState } from 'react'
import Button from '../../../common/components/marketing/buttons/Button'
import { TextInputWithLabel } from '@/src/common/components/elements/Forms/TextInputWithLabel'
import { ValidationError } from 'yup'
import { BuildACardStep, SimpleFieldWrapper } from '../StepElement'
import { FormErrorDisplay } from '../FormErrorDisplay'
import { TextAreaInputWithLabel } from '@/src/common/components/elements/Forms/TextAreaInputWithLabel'
import {
    CARRIER_FONT_HANDWRITTEN,
    carrierTextMax,
    nameMax,
    VALIDATE_RECIPIENTS,
} from '../constants'
import PurchaseFlowMaxAlert from '@/src/common/components/elements/Alerts/PurchaseFlowMaxAlert'
import { step2schema } from '@/src/modules/build-a-card/validations/schemas'
import ShippingInfoText from '@/src/modules/build-a-card/content/ShippingInfoText'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import HandwrittenInput from '@/src/common/components/elements/Inputs/HandwrittenInput'
import HandwrittenModal from '@/src/modules/build-a-card/modals/HandwrittenModal'
import { usePlasticGiftQuantityContext } from '@/src/common/contexts/PlasticGiftQuantityContext'
import { cleanCardFieldInput } from '@/src/common/utilities/handleEmoji'
import { HeaderParagraphText } from '@/src/modules/build-a-card/StyledComponents'
import { sendBasicEvent } from '@/src/common/utilities/gtag'
import { FieldWrapperWithGap } from '@/src/common/components/elements/Forms/StyledComponents'

export const CarrierOrGreetingCardPersonalizationStep = () => {
    const { currentStep, handwrittenEnabled, hasGreetingCard, fontSelection } =
        useBasicGiftCardContext()
    const {
        showHandwrittenModal,
        setShowHandwrittenModal,
        validateAndAddCart,
        displayLiveErrors,
        isLoading,
        setIsLoading,
        continueIsLoading,
        setContinueIsLoading,
        KYCMinMaxLimits,
    } = usePlasticGiftQuantityContext()

    const { giftState, dispatch } = usePlasticGiftQuantityContext()
    const { isEditing, listOfRecipients, setListOfRecipients } = giftState
    const { loadAmountMeetsKYCMin, numberOfCardsMeetsKYCMin } = KYCMinMaxLimits
    const [formErrors, setFormErrors] = useState<ValidationError[]>([])
    const showHandwrittenOption: boolean = handwrittenEnabled && hasGreetingCard
    const isHandwritten: boolean = fontSelection === CARRIER_FONT_HANDWRITTEN

    useEffect(() => {
        if (hasGreetingCard) {
            sendBasicEvent(
                isHandwritten
                    ? 'handwritten_selected'
                    : 'standard_font_selected'
            )
        }
    }, [fontSelection, isHandwritten, hasGreetingCard])

    useEffect(() => {
        if (!displayLiveErrors) {
            return
        }
        step2schema(hasGreetingCard)
            .validate(
                {
                    recipientName: listOfRecipients[0].recipientName,
                    senderName: listOfRecipients[0].senderName,
                    recipientMessage: listOfRecipients[0].recipientMessage,
                },
                {
                    abortEarly: false,
                    context: { hasGreetingCard: hasGreetingCard },
                }
            )
            .then(() => {
                setFormErrors([])
            })
            .catch((err) => {
                setFormErrors(err.inner)
            })
            .finally(() => {
                dispatch({ type: VALIDATE_RECIPIENTS, payload: {} })
            })
    }, [
        dispatch,
        displayLiveErrors,
        listOfRecipients,
        isHandwritten,
        hasGreetingCard,
    ])

    return (
        <BuildACardStep active={currentStep === 3 || currentStep === 4}>
            {showHandwrittenOption && (
                <SimpleFieldWrapper>
                    <HandwrittenModal
                        showModal={showHandwrittenModal}
                        setShowModal={setShowHandwrittenModal}
                    />
                    <HandwrittenInput
                        formErrors={formErrors}
                        setFormErrors={setFormErrors}
                    />
                </SimpleFieldWrapper>
            )}
            <SimpleFieldWrapper>
                <HeaderParagraphText>
                    Customize text in{' '}
                    {hasGreetingCard ? 'greeting card' : 'carrier'}:
                </HeaderParagraphText>
                <FieldWrapperWithGap>
                    {!hasGreetingCard && (
                        <>
                            {/*@todo switch to use NameInput when ready for multi shipments*/}
                            <TextInputWithLabel
                                isRequired={true}
                                id={'recipientName'}
                                label={'To'}
                                placeholder={'Add recipient name'}
                                value={listOfRecipients[0].recipientName}
                                maxLength={nameMax}
                                onChange={(e: BaseSyntheticEvent) => {
                                    const trimmedValue = cleanCardFieldInput(
                                        e.currentTarget.value
                                    )

                                    const list = [...listOfRecipients]
                                    list.forEach((recipient, index) => {
                                        recipient.recipientName = trimmedValue
                                    })
                                    setListOfRecipients(list)
                                }}
                            />
                            <FormErrorDisplay
                                formErrors={formErrors}
                                path={'recipientName'}
                                variant={'error-message'}
                            />
                        </>
                    )}
                    {/*@todo add new input field that mimics NameInput*/}
                    <TextAreaInputWithLabel
                        isRequired={true}
                        label={'Message'}
                        maxLength={carrierTextMax}
                        placeholder={'Add a message...'}
                        value={listOfRecipients[0].recipientMessage}
                        onChange={(e: BaseSyntheticEvent) => {
                            const trimmedValue = cleanCardFieldInput(
                                e.currentTarget.value
                            )
                            const list = [...listOfRecipients]
                            list.forEach((recipient, index) => {
                                recipient.recipientMessage = trimmedValue
                            })
                            setListOfRecipients(list)
                        }}
                        charCounter={true}
                        removeEmojis={true}
                        charMax={carrierTextMax}
                        style={{
                            whiteSpace: 'pre-line',
                        }}
                    />
                    <FormErrorDisplay
                        formErrors={formErrors}
                        path={'recipientMessage'}
                        variant={'error-message'}
                    />
                </FieldWrapperWithGap>
            </SimpleFieldWrapper>
            {!hasGreetingCard && (
                <SimpleFieldWrapper>
                    {/*add new input field that mimics NameInput*/}
                    <TextInputWithLabel
                        isRequired={true}
                        label={'From'}
                        placeholder={'Add sender name'}
                        value={listOfRecipients[0].senderName}
                        maxLength={nameMax}
                        onChange={(e: BaseSyntheticEvent) => {
                            const trimmedValue = cleanCardFieldInput(
                                e.currentTarget.value
                            )
                            const list = [...listOfRecipients]
                            list.forEach((recipient, index) => {
                                recipient.senderName = trimmedValue
                            })
                            setListOfRecipients(list)
                        }}
                    />
                    <FormErrorDisplay
                        formErrors={formErrors}
                        path={'senderName'}
                    />
                </SimpleFieldWrapper>
            )}
            {(loadAmountMeetsKYCMin || numberOfCardsMeetsKYCMin) &&
                !isLoading && <PurchaseFlowMaxAlert />}
            <SimpleFieldWrapper>
                <Button
                    label={
                        !isEditing ? 'Add to cart & checkout' : 'Save changes'
                    }
                    showArrow={true}
                    onClick={async () => {
                        setIsLoading(true)
                        await validateAndAddCart({ keepShopping: false })
                    }}
                    textTransform={'uppercase'}
                    width={'wide'}
                    isLoading={isLoading}
                    disabled={isLoading || continueIsLoading}
                />
                <Button
                    variant={'primaryOutline'}
                    label={
                        !isEditing
                            ? 'Add to cart & keep shopping'
                            : 'Save & keep shopping'
                    }
                    showArrow={true}
                    onClick={async () => {
                        setContinueIsLoading(true)
                        await validateAndAddCart({ keepShopping: true })
                    }}
                    textTransform={'uppercase'}
                    width={'wide'}
                    isLoading={continueIsLoading}
                    disabled={isLoading || continueIsLoading}
                    style={{ marginTop: 10 }}
                />
            </SimpleFieldWrapper>
            <ShippingInfoText />
        </BuildACardStep>
    )
}
